import { ContentType } from '../contentByLabelTypes.ts';
import type { ContentByLabelItem, ContentByLabelResult } from '../contentByLabelTypes.ts';

import type { useContentByLabelDataQuery$data } from './__generated__/useContentByLabelDataQuery.graphql.ts';

type RawLabels = NonNullable<
	NonNullable<NonNullable<useContentByLabelDataQuery$data['search']>['nodes']>[number]
>['content'];

type RawContent = NonNullable<RawLabels>;

export function determineContentType(
	type: RawContent['type'] | null | undefined,
	subType: RawContent['subType'] | null | undefined,
): ContentType {
	if (type === 'blogpost') {
		return ContentType.Blogpost;
	}
	if (type === 'liveDoc' || (type === 'page' && subType === 'live')) {
		return ContentType.LivePage;
	}
	return ContentType.Page;
}

function extractLabels(content: RawLabels): ContentByLabelItem['labels'] {
	const labels: ContentByLabelItem['labels'] = [];
	const rawLabels = content?.labels?.nodes;
	if (rawLabels) {
		for (const label of rawLabels) {
			if (label != null) {
				labels.push({ name: label.name ?? '' });
			}
		}
	}
	return labels;
}

/**
 * Processes raw GraphQL query data from useContentByLabelDataQuery into a
 * structured ContentByLabelResult, transforming nested search nodes into a
 * flat list of ContentByLabelItem objects.
 */
export function useProcessedContentByLabelsResult(
	data: useContentByLabelDataQuery$data | undefined,
): ContentByLabelResult | undefined {
	if (!data?.search?.nodes) {
		return undefined;
	}

	const items: ContentByLabelItem[] = [];

	for (const node of data.search.nodes) {
		if (node == null) {
			continue;
		}

		items.push({
			id: node.content?.id ?? '',
			title: node.title ?? '',
			type: determineContentType(node.content?.type, node.content?.subType),
			url: node.url ? `/wiki${node.url}` : '',
			space: {
				id: node.content?.space?.id ?? '',
				key: node.content?.space?.key ?? '',
				name: node.content?.space?.name ?? '',
			},
			excerpt: node.content?.macroExcerpt?.atlasDocFormat ?? '',
			mediaToken: node.content?.body?.atlas_doc_format?.mediaToken?.token ?? '',
			labels: extractLabels(node.content),
		});
	}

	return { items };
}
